import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import StatusBadge from '../../components/common/StatusBadge';

const AREA_LABELS = {
  personal: '👤 Personal Information',
  home: '🏠 Home Address',
  business: '🏢 Business Details',
  collateral: '🔒 Collateral',
  documents: '📄 KYC Documents',
};

export default function OfficerReturnedClients() {
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('newest');
  const [expandedId, setExpandedId] = useState(null);
  const [actionLoading, setActiveActionId] = useState(null);

  useEffect(() => {
    fetchReturnedClients();
  }, []);

  const fetchReturnedClients = async () => {
    setLoading(true);
    try {
      // Try fetching with the "returned-for-modification" status
      const res = await api.get('/clients?status=returned-for-modification&limit=200');
      const data = res.data?.data?.clients || res.data?.clients || [];
      setClients(data);
    } catch (err) {
      console.error('Failed to fetch returned clients:', err);
      // Fallback: try alternate status
      try {
        const res2 = await api.get('/clients?status=returned&limit=200');
        const data2 = res2.data?.data?.clients || res2.data?.clients || [];
        setClients(data2);
      } catch {
        setClients([]);
      }
    }
    setLoading(false);
  };

  const handleResumeEditing = (client) => {
    // If client has a lead, navigate to onboarding
    // Otherwise navigate to the client edit page
    if (client.leadId || client.lead) {
      const leadId = typeof client.leadId === 'object' ? client.leadId._id : client.leadId;
      if (leadId) {
        navigate(`/clients/onboard/${leadId}`);
        return;
      }
    }
    // Open the client portfolio; all profile sections that the officer may update are available there.
    navigate(`/clients/${client._id}`, { state: { returnedForModification: true } });
  };

  const handleViewProfile = (clientId) => {
    navigate(`/clients/${clientId}`);
  };

  const handleResubmit = async (client) => {
    if (!window.confirm('Have you saved all requested profile changes? Resubmit this client to the manager for approval?')) {
      return;
    }
    setActiveActionId(client._id);
    try {
      await api.patch(`/clients/${client._id}/resubmit`, {
        decision: 'resubmitted',
        reason: 'Officer has completed requested modifications and is resubmitting for approval.',
      });
      // Update local state to remove the resubmitted client
      setClients(prev => prev.filter(c => c._id !== client._id));
      alert('✅ Client resubmitted for manager approval successfully!');
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to resubmit client');
    } finally {
      setActiveActionId(null);
    }
  };

  const getFilteredClients = () => {
    let filtered = clients;

    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase().trim();
      filtered = filtered.filter(c =>
        (c.firstName?.toLowerCase() || '').includes(term) ||
        (c.lastName?.toLowerCase() || '').includes(term) ||
        (c.clientNumber?.toLowerCase() || '').includes(term) ||
        (c.phone?.toLowerCase() || '').includes(term) ||
        (`${c.firstName || ''} ${c.lastName || ''}`.toLowerCase()).includes(term)
      );
    }

    switch (sortBy) {
      case 'oldest':
        filtered = [...filtered].sort((a, b) => new Date(a.returnedAt || a.updatedAt) - new Date(b.returnedAt || b.updatedAt));
        break;
      case 'name':
        filtered = [...filtered].sort((a, b) => {
          const nameA = `${a.firstName || ''} ${a.lastName || ''}`.toLowerCase();
          const nameB = `${b.firstName || ''} ${b.lastName || ''}`.toLowerCase();
          return nameA.localeCompare(nameB);
        });
        break;
      case 'newest':
      default:
        filtered = [...filtered].sort((a, b) => new Date(b.returnedAt || b.updatedAt) - new Date(a.returnedAt || a.updatedAt));
        break;
    }

    return filtered;
  };

  const filteredClients = getFilteredClients();

  const timeAgo = (date) => {
    if (!date) return 'Unknown';
    const diff = Math.floor((new Date() - new Date(date)) / 1000);
    if (diff < 60) return 'Just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
    return new Date(date).toLocaleDateString();
  };

  const inputStyle = {
    width: '100%', padding: '10px 12px', borderRadius: '8px',
    border: '1px solid #d1d5db', fontSize: '14px', outline: 'none', background: '#fff'
  };

  if (loading) return <div className="spinner"></div>;

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h3 style={{ margin: 0 }}>↩️ Returned for Modification</h3>
          <p style={{ margin: '4px 0 0', fontSize: 13, color: '#6b7280' }}>
            Client profiles returned by the manager that need your attention
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <span style={{
            padding: '4px 12px', borderRadius: 20, fontSize: 12, fontWeight: 600,
            background: clients.length > 0 ? '#fef3c7' : '#dcfce7',
            color: clients.length > 0 ? '#92400e' : '#166534',
          }}>
            {clients.length} returned profile{clients.length !== 1 ? 's' : ''}
          </span>
          <button className="btn btn-outline btn-sm" onClick={fetchReturnedClients}>🔄 Refresh</button>
          <button className="btn btn-secondary btn-sm" onClick={() => navigate('/clients')}>← Back to Clients</button>
        </div>
      </div>

      {clients.length === 0 ? (
        <div className="card" style={{ textAlign: 'center', padding: 60 }}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>✅</div>
          <h4 style={{ color: '#6b7280', margin: '0 0 8px' }}>No Returned Profiles</h4>
          <p style={{ color: '#9ca3af', margin: '0 0 20px' }}>
            You have no client profiles that need modification. New returned profiles from the manager will appear here.
          </p>
          <button className="btn btn-primary" onClick={() => navigate('/leads')}>Start New Onboarding</button>
        </div>
      ) : (
        <div className="card">
          {/* Search and sort bar */}
          <div style={{
            padding: '16px 16px 12px',
            borderBottom: '1px solid #e2e8f0',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 12
          }}>
            <div style={{ position: 'relative' }}>
              <input
                type="text"
                placeholder="Search by name, ID, phone..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{
                  ...inputStyle,
                  width: '280px',
                  padding: '8px 12px 8px 36px',
                  fontSize: '13px',
                }}
              />
              <span style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: '#9ca3af', fontSize: 14 }}>
                🔍
              </span>
            </div>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              style={{
                padding: '8px 12px',
                borderRadius: '8px',
                border: '1px solid #d1d5db',
                fontSize: '13px',
                cursor: 'pointer',
                background: '#fff',
              }}
            >
              <option value="newest">Newest First</option>
              <option value="oldest">Oldest First</option>
              <option value="name">Name A-Z</option>
            </select>
          </div>

          {/* List of returned clients */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: 16 }}>
            {filteredClients.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 32, color: '#64748b' }}>
                <div style={{ fontSize: 32, marginBottom: 8 }}>🔍</div>
                No clients found matching your search.
              </div>
            ) : (
              filteredClients.map(client => {
                const isExpanded = expandedId === client._id;
                const returnAreas = client.returnAreas || [];
                const returnedBy = client.returnedBy;
                const returnedAt = client.returnedAt || client.updatedAt;

                return (
                  <div
                    key={client._id}
                    style={{
                      border: '1px solid #fed7aa',
                      borderRadius: 12,
                      overflow: 'hidden',
                      borderLeft: '4px solid #f97316',
                      background: '#fff',
                    }}
                  >
                    {/* Top section: Client info + actions */}
                    <div style={{ padding: 20 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12 }}>
                        <div style={{ flex: 1, minWidth: 250 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10 }}>
                            <div style={{
                              width: 48, height: 48, borderRadius: 12,
                              background: '#fff7ed', color: '#c2410c',
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              fontSize: 20, fontWeight: 700,
                            }}>
                              {(client.firstName?.[0] || '?')}{(client.lastName?.[0] || '')}
                            </div>
                            <div>
                              <h4 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>
                                {client.firstName || 'Unknown'} {client.lastName || 'Client'}
                              </h4>
                              <p style={{ margin: 0, fontSize: 12, color: '#6b7280' }}>
                                <span style={{ fontFamily: 'monospace', fontWeight: 600 }}>{client.clientNumber || 'N/A'}</span>
                                {client.phone && <span style={{ marginLeft: 8 }}>• 📞 {client.phone}</span>}
                              </p>
                            </div>
                            <div>
                              <StatusBadge
                                status={client.status}
                                map={{ 'returned-for-modification': 'warning', 'returned': 'warning' }}
                              />
                            </div>
                          </div>

                          {/* Manager's instructions preview */}
                          <div style={{
                            padding: '12px 14px',
                            background: '#fff7ed',
                            borderRadius: 8,
                            border: '1px solid #fed7aa',
                            fontSize: 13,
                            color: '#9a3412',
                            marginBottom: 10,
                          }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
                              <span style={{ fontSize: 14 }}>📌</span>
                              <strong style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                Manager's Instructions
                              </strong>
                              <span style={{ marginLeft: 'auto', fontSize: 11, color: '#9ca3af', fontWeight: 500 }}>
                                {timeAgo(returnedAt)}
                              </span>
                            </div>
                            <p style={{ margin: 0, fontSize: 13, lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>
                              {client.returnReason || client.notes || 'No specific instructions provided. Please review the entire profile and contact the manager if unclear.'}
                            </p>
                          </div>

                          {/* Areas to fix badges */}
                          {returnAreas.length > 0 && (
                            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                              <span style={{ fontSize: 11, color: '#6b7280', fontWeight: 600, marginRight: 4, alignSelf: 'center' }}>
                                Areas to fix:
                              </span>
                              {returnAreas.map(area => (
                                <span key={area} style={{
                                  padding: '3px 10px',
                                  borderRadius: 12,
                                  fontSize: 11,
                                  fontWeight: 600,
                                  background: '#fef3c7',
                                  color: '#92400e',
                                  border: '1px solid #fde68a',
                                }}>
                                  {AREA_LABELS[area] || area}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>

                        {/* Action buttons */}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 180 }}>
                          <button
                            className="btn btn-primary btn-sm"
                            onClick={() => handleResumeEditing(client)}
                            style={{ fontWeight: 600 }}
                          >
                            ✏️ Resume Editing
                          </button>
                          <button
                            className="btn btn-outline btn-sm"
                            onClick={() => handleViewProfile(client._id)}
                          >
                            👁️ View Full Profile
                          </button>
                          <button
                            className="btn btn-sm"
                            onClick={() => setExpandedId(isExpanded ? null : client._id)}
                            style={{
                              background: '#fff',
                              color: '#6366f1',
                              border: '1px solid #c7d2fe',
                              fontWeight: 500,
                            }}
                          >
                            {isExpanded ? '🔼 Hide Details' : '🔽 View Details'}
                          </button>
                          <button
                            className="btn btn-success btn-sm"
                            onClick={() => handleResubmit(client)}
                            disabled={actionLoading === client._id}
                            style={{ fontWeight: 600 }}
                          >
                            {actionLoading === client._id ? '⏳ Submitting...' : '✅ Resubmit for Approval'}
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* Expanded details */}
                    {isExpanded && (
                      <div style={{
                        padding: '16px 20px',
                        background: '#f8fafc',
                        borderTop: '1px solid #e2e8f0',
                      }}>
                        <h5 style={{ margin: '0 0 12px', fontSize: 13, color: '#374151', fontWeight: 600 }}>
                          📋 Profile Summary
                        </h5>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 8 }}>
                          <div style={{ padding: '8px 12px', background: '#fff', borderRadius: 6, border: '1px solid #e5e7eb' }}>
                            <span style={{ fontSize: 10, color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase', display: 'block', marginBottom: 2 }}>
                              National ID
                            </span>
                            <span style={{ fontSize: 13, color: '#1f2937', fontWeight: 500, fontFamily: 'monospace' }}>
                              {client.clientNumber || 'N/A'}
                            </span>
                          </div>
                          <div style={{ padding: '8px 12px', background: '#fff', borderRadius: 6, border: '1px solid #e5e7eb' }}>
                            <span style={{ fontSize: 10, color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase', display: 'block', marginBottom: 2 }}>
                              Phone
                            </span>
                            <span style={{ fontSize: 13, color: '#1f2937', fontWeight: 500 }}>
                              {client.phone || 'N/A'}
                            </span>
                          </div>
                          <div style={{ padding: '8px 12px', background: '#fff', borderRadius: 6, border: '1px solid #e5e7eb' }}>
                            <span style={{ fontSize: 10, color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase', display: 'block', marginBottom: 2 }}>
                              Gender
                            </span>
                            <span style={{ fontSize: 13, color: '#1f2937', fontWeight: 500, textTransform: 'capitalize' }}>
                              {client.gender || 'N/A'}
                            </span>
                          </div>
                          <div style={{ padding: '8px 12px', background: '#fff', borderRadius: 6, border: '1px solid #e5e7eb' }}>
                            <span style={{ fontSize: 10, color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase', display: 'block', marginBottom: 2 }}>
                              Occupation
                            </span>
                            <span style={{ fontSize: 13, color: '#1f2937', fontWeight: 500 }}>
                              {client.occupation || 'N/A'}
                            </span>
                          </div>
                          <div style={{ padding: '8px 12px', background: '#fff', borderRadius: 6, border: '1px solid #e5e7eb' }}>
                            <span style={{ fontSize: 10, color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase', display: 'block', marginBottom: 2 }}>
                              Monthly Income
                            </span>
                            <span style={{ fontSize: 13, color: '#1f2937', fontWeight: 500 }}>
                              {client.monthlyIncome ? `KES ${Number(client.monthlyIncome).toLocaleString()}` : 'N/A'}
                            </span>
                          </div>
                          <div style={{ padding: '8px 12px', background: '#fff', borderRadius: 6, border: '1px solid #e5e7eb' }}>
                            <span style={{ fontSize: 10, color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase', display: 'block', marginBottom: 2 }}>
                              Business Name
                            </span>
                            <span style={{ fontSize: 13, color: '#1f2937', fontWeight: 500 }}>
                              {client.businessName || 'N/A'}
                            </span>
                          </div>
                          <div style={{ padding: '8px 12px', background: '#fff', borderRadius: 6, border: '1px solid #e5e7eb' }}>
                            <span style={{ fontSize: 10, color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase', display: 'block', marginBottom: 2 }}>
                              Home City
                            </span>
                            <span style={{ fontSize: 13, color: '#1f2937', fontWeight: 500 }}>
                              {client.address?.city || 'N/A'}
                            </span>
                          </div>
                          <div style={{ padding: '8px 12px', background: '#fff', borderRadius: 6, border: '1px solid #e5e7eb' }}>
                            <span style={{ fontSize: 10, color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase', display: 'block', marginBottom: 2 }}>
                              Branch
                            </span>
                            <span style={{ fontSize: 13, color: '#1f2937', fontWeight: 500 }}>
                              {client.branch?.name || 'Unassigned'}
                            </span>
                          </div>
                          <div style={{ padding: '8px 12px', background: '#fff', borderRadius: 6, border: '1px solid #e5e7eb' }}>
                            <span style={{ fontSize: 10, color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase', display: 'block', marginBottom: 2 }}>
                              Assigned To
                            </span>
                            <span style={{ fontSize: 13, color: '#1f2937', fontWeight: 500 }}>
                              {client.assignedTo ? `${client.assignedTo.firstName} ${client.assignedTo.lastName}` : 'Unassigned'}
                            </span>
                          </div>
                          <div style={{ padding: '8px 12px', background: '#fff', borderRadius: 6, border: '1px solid #e5e7eb' }}>
                            <span style={{ fontSize: 10, color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase', display: 'block', marginBottom: 2 }}>
                              Returned At
                            </span>
                            <span style={{ fontSize: 13, color: '#1f2937', fontWeight: 500 }}>
                              {returnedAt ? new Date(returnedAt).toLocaleString() : 'N/A'}
                            </span>
                          </div>
                        </div>

                        {/* Return metadata */}
                        <div style={{
                          marginTop: 14,
                          padding: '12px 14px',
                          background: '#fff',
                          borderRadius: 8,
                          border: '1px solid #fed7aa',
                        }}>
                          <h6 style={{ fontSize: 12, color: '#6b7280', margin: '0 0 8px', fontWeight: 600, textTransform: 'uppercase' }}>
                            ↩️ Return Information
                          </h6>
                          <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', fontSize: 12 }}>
                            <div>
                              <span style={{ color: '#9ca3af' }}>Returned by: </span>
                              <span style={{ fontWeight: 600, color: '#9a3412' }}>
                                {typeof returnedBy === 'object'
                                  ? `${returnedBy?.firstName || ''} ${returnedBy?.lastName || 'Manager'}`
                                  : returnedBy || 'Manager'}
                              </span>
                            </div>
                            <div>
                              <span style={{ color: '#9ca3af' }}>Date: </span>
                              <span style={{ fontWeight: 500, color: '#1f2937' }}>
                                {returnedAt ? new Date(returnedAt).toLocaleString() : 'N/A'}
                              </span>
                            </div>
                          </div>
                        </div>

                        {/* Action reminder */}
                        <div style={{
                          marginTop: 14,
                          padding: '12px 14px',
                          borderRadius: 8,
                          background: '#eff6ff',
                          border: '1px solid #bfdbfe',
                          fontSize: 12,
                          color: '#1e40af',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 8,
                        }}>
                          <span style={{ fontSize: 16 }}>💡</span>
                          <span>
                            <strong>Next Steps:</strong> Click "Resume Editing" to open the onboarding form and make the requested modifications. Once done, click "Resubmit for Approval" to send it back to the manager.
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>

          {/* Footer */}
          <div style={{
            padding: '10px 16px',
            borderTop: '1px solid #e2e8f0',
            fontSize: '12px',
            color: '#64748b',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}>
            <span>
              Showing {filteredClients.length} of {clients.length} returned profile(s)
            </span>
            <span>
              {clients.length} need modification
            </span>
          </div>
        </div>
      )}
    </div>
  );
}